import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import Button from '../components/Button';
import Alert from '../components/Alert';

// ADMIN EMAIL - Only this email can use this page
const ADMIN_EMAIL = 'stro.alexander@gmail.com';

const AdminSetupPage = () => {
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const isAuthorizedAdmin = currentUser?.email === ADMIN_EMAIL;

  const handleSetupAdmin = async () => {
    if (!isAuthorizedAdmin) {
      setError('Unauthorized. Only the designated admin can use this page.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Update user document with admin privileges
      await updateDoc(doc(db, 'users', currentUser.uid), {
        isAdmin: true,
        subscriptionTier: 'enterprise',
        subscriptionStatus: 'active',
        auditsUsed: 0,
        auditsLimit: 999999,
        adminSetupAt: new Date().toISOString()
      });

      // Refresh user data in context
      await refreshUserData();

      setSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (err) {
      console.error('Admin setup error:', err);
      setError(err.message || 'Failed to set up admin privileges');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Admin Setup</h1>
          <p className="text-gray-600 mb-6">Please sign in first to set up admin privileges.</p>
          <Button onClick={() => navigate('/login')} fullWidth>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (!isAuthorizedAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⛔</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-2">This page is restricted to authorized administrators only.</p>
          <p className="text-sm text-gray-500 mb-6">Signed in as: {currentUser.email}</p>
          <Button onClick={() => navigate('/dashboard')} fullWidth variant="secondary">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🛡️</div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Setup</h1>
          <p className="text-gray-600 mt-2">Configure your administrator account</p>
        </div>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        {success ? (
          <div className="text-center">
            <Alert type="success" message="Admin privileges activated successfully!" />
            <p className="mt-4 text-gray-600">Redirecting to dashboard...</p>
          </div>
        ) : (
          <>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-indigo-900 mb-2">Admin Privileges Include:</h3>
              <ul className="text-sm text-indigo-800 space-y-1">
                <li>✓ Unlimited audits</li>
                <li>✓ Enterprise subscription tier</li>
                <li>✓ Full platform access</li>
                <li>✓ Admin dashboard access</li>
              </ul>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                <strong>Account:</strong> {currentUser.email}
              </p>
            </div>

            <Button
              onClick={handleSetupAdmin}
              loading={loading}
              disabled={loading}
              fullWidth
              size="lg"
            >
              Activate Admin Privileges
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSetupPage;
